import React, { useState, useEffect } from 'react';
import { supabase } from '../../../config/supabase';
import './PaymentMethods.css';

const PaymentMethods = () => {
  const [paymentData, setPaymentData] = useState({
    cod: {
      orders: 0,
      percentage: 0,
      amount: 0,
      avg: 0
    },
    online: {
      orders: 0,
      percentage: 0,
      amount: 0,
      avg: 0
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPaymentData();
  }, []);

  const fetchPaymentData = async () => {
    setLoading(true);
    try {
      const { data: orders, error } = await supabase
        .from('orders')
        .select('payment_method, total')
        .order('created_at', { ascending: false })
        .limit(1000); // Get recent 1000 orders

      if (error) throw error;

      // Aggregate by payment method
      const codOrders = (orders || []).filter(o => 
        o.payment_method?.toLowerCase().includes('cash') || 
        o.payment_method?.toLowerCase().includes('cod') ||
        !o.payment_method
      );
      const onlineOrders = (orders || []).filter(o => 
        o.payment_method?.toLowerCase().includes('card') || 
        o.payment_method?.toLowerCase().includes('online')
      );

      const codAmount = codOrders.reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
      const onlineAmount = onlineOrders.reduce((sum, o) => sum + parseFloat(o.total || 0), 0);
      const totalOrders = orders?.length || 0;
      const totalAmount = codAmount + onlineAmount;

      const codPercentage = totalOrders > 0 ? Math.round((codOrders.length / totalOrders) * 100) : 0;
      const onlinePercentage = totalOrders > 0 ? Math.round((onlineOrders.length / totalOrders) * 100) : 0;

      setPaymentData({
        cod: {
          orders: codOrders.length,
          percentage: codPercentage,
          amount: Math.round(codAmount),
          avg: codOrders.length > 0 ? Math.round(codAmount / codOrders.length) : 0
        },
        online: {
          orders: onlineOrders.length,
          percentage: onlinePercentage,
          amount: Math.round(onlineAmount),
          avg: onlineOrders.length > 0 ? Math.round(onlineAmount / onlineOrders.length) : 0
        }
      });
    } catch (error) {
      console.error('Error fetching payment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalOrders = paymentData.cod.orders + paymentData.online.orders;
  const totalAmount = paymentData.cod.amount + paymentData.online.amount;

  const formatCurrency = (amount) => {
    return `Rs. ${amount.toLocaleString()}`;
  };

  // Calculate angles for pie chart
  const codAngle = (paymentData.cod.percentage / 100) * 360;
  const onlineAngle = (paymentData.online.percentage / 100) * 360;

  const getArcPath = (startAngle, endAngle, radius = 80) => {
    const start = polarToCartesian(100, 100, radius, startAngle);
    const end = polarToCartesian(100, 100, radius, endAngle);
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
    
    return [
      `M 100 100`,
      `L ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
      'Z'
    ].join(' ');
  };

  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 className="chart-title">Payment Methods Distribution</h3>
        </div>
      </div>

      <div className="payment-methods-content">
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6C757D' }}>Loading data...</div>
        ) : (
          <>
        <div className="payment-chart-wrapper">
          <svg viewBox="0 0 200 200" className="pie-chart-svg">
            <path
              d={getArcPath(-90, -90 + codAngle)}
              fill="#fe0000"
              className="pie-segment"
            />
            <path
              d={getArcPath(-90 + codAngle, -90 + codAngle + onlineAngle)}
              fill="#ff3333"
              className="pie-segment"
            />
            <circle cx="100" cy="100" r="50" fill="#FFFFFF" />
            <text x="100" y="95" textAnchor="middle" className="pie-center-text">
              {totalOrders}
            </text>
            <text x="100" y="110" textAnchor="middle" className="pie-center-label">
              Total Orders
            </text>
          </svg>
        </div>

        <div className="payment-breakdown">
          <table className="payment-table">
            <thead>
              <tr>
                <th>Method</th>
                <th>Orders</th>
                <th>Amount</th>
                <th>Avg</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <div className="payment-method-cell">
                    <div className="method-color-dot cod"></div>
                    <span>Cash on Delivery</span>
                  </div>
                </td>
                <td>
                  <div className="payment-stat">
                    <span className="stat-number">{paymentData.cod.orders}</span>
                    <span className="stat-percentage">({paymentData.cod.percentage}%)</span>
                  </div>
                </td>
                <td>{formatCurrency(paymentData.cod.amount)}</td>
                <td>{formatCurrency(paymentData.cod.avg)}</td>
              </tr>
              <tr>
                <td>
                  <div className="payment-method-cell">
                    <div className="method-color-dot online"></div>
                    <span>Online Payment</span>
                  </div>
                </td>
                <td>
                  <div className="payment-stat">
                    <span className="stat-number">{paymentData.online.orders}</span>
                    <span className="stat-percentage">({paymentData.online.percentage}%)</span>
                  </div>
                </td>
                <td>{formatCurrency(paymentData.online.amount)}</td>
                <td>{formatCurrency(paymentData.online.avg)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default PaymentMethods;

